/**
 * Desktop: the huddle call window (apps/desktop/src-tauri/src/ring.rs; design "Huddle ring", Main board) instead of the
 * in-window card. The shell that got the ring shows it there (`ring_show`) and hides it when the ring is over
 * (`ring_hide`); the window's buttons come back as `bundle:ring-action` to this window. The Dock icon bounces while it
 * rings (requestUserAttention Critical, cancelled when it stops); the ringtone keeps looping in this webview.
 */
import { isDesktop } from "./desktop.ts";

/** What the call window draws (Rust `ring::Ring`). */
export type RingShown = {
  huddleId: string;
  title: string;
  body: string;
  label: string;
  callerName: string;
  callerPhoto?: string;
  callerTone?: number;
  callerEmoji?: string;
  mic: boolean;
  camera: boolean;
};

export type RingAction = { huddleId: string; action: "join" | "decline" | "message" | "close"; mic: boolean; camera: boolean };

export const RING_ACTION_EVENT = "bundle:ring-action";
export const RING_STATE_EVENT = "bundle:ring";

/** The desktop draws rings in its own window (every desktop platform; Slack's huddle ring does the same). */
export function ringsInWindow(): boolean {
  return isDesktop();
}

async function core() {
  return import("@tauri-apps/api/core");
}

async function attention(on: boolean): Promise<void> {
  const { getCurrentWindow, UserAttentionType } = await import("@tauri-apps/api/window");
  const win = getCurrentWindow();
  if (on && (await win.isFocused())) return;
  await win.requestUserAttention(on ? UserAttentionType.Critical : null);
}

/**
 * Shows the call window for `ring` (`onFailed`: it couldn't, e.g. an older shell: the notification backup); the
 * returned function hides it (ring over) and stops the Dock bounce.
 */
export function showRingWindow(ring: RingShown, onFailed: () => void): () => void {
  void core()
    .then(({ invoke }) => invoke("ring_show", { ring }))
    .catch((err: unknown) => {
      console.warn("call window", err);
      onFailed();
    });
  void attention(true).catch(() => undefined);
  return () => {
    void core()
      .then(({ invoke }) => invoke("ring_hide", { huddleId: ring.huddleId }))
      .catch(() => undefined);
    void attention(false).catch(() => undefined);
  };
}

/** This window's `bundle:ring-action` events (the call window's buttons). */
export async function onRingAction(handler: (a: RingAction) => void): Promise<() => void> {
  const { getCurrentWebviewWindow } = await import("@tauri-apps/api/webviewWindow");
  return getCurrentWebviewWindow().listen<RingAction>(RING_ACTION_EVENT, (e) => handler(e.payload));
}

/** In the call window: the ring to draw now, then every change (`null`: nothing). */
export async function watchRing(set: (ring: RingShown | null) => void): Promise<() => void> {
  const [{ invoke }, { getCurrentWebviewWindow }] = await Promise.all([core(), import("@tauri-apps/api/webviewWindow")]);
  const off = await getCurrentWebviewWindow().listen<RingShown | null>(RING_STATE_EVENT, (e) => set(e.payload));
  set(await invoke<RingShown | null>("ring_state"));
  return off;
}

/** In the call window: a button. */
export function sendRingAction(action: RingAction["action"], mic: boolean, camera: boolean): void {
  void core()
    .then(({ invoke }) => invoke("ring_action", { action, mic, camera }))
    .catch((err: unknown) => console.warn("call window action", err));
}
